import { computed, inject, InjectionToken, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestStatus } from '@core/tellent-company-verification-types';
// eslint-disable-next-line boundaries/element-types
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { TellentNavigationRoutingService } from '@core/tellent-logic';
import { Org, Seat, TellentCurrentUser, TellentMember, UserProfileTab } from '@core/tellent-types';

import { TELLENT_PROFILE_DIALOG_ID } from '../current-user-profile-dialog/current-user-profile-tellent-client-route';

type TellentProfileDropdownConfig = {
  companies: Signal<TellentMember[]>;
  currentUser: Signal<TellentCurrentUser>;
  currentMember: Signal<TellentMember | undefined>;
  currentOrg: Signal<Org | undefined>;
  currentSeat: Signal<Seat | undefined>;
  companyVerificationStatus: Signal<CompanyVerificationRequestStatus | undefined>;
  handlers: {
    switchCompany: (orgGuid: string) => void;
    editProfile: () => void;
    manageCompanies: () => void;
    /**
     * Opens the host app's push notification settings view. Only relevant
     * inside the tellent-hr-mobile shell, where the dropdown shows the
     * "Push notifications" item.
     */
    openPushNotificationSettings?: () => void;
  };
};

type TellentProfileDropdownConfigFactory = () => TellentProfileDropdownConfig;

export const TellentProfileDropdownConfigToken = new InjectionToken<TellentProfileDropdownConfig>(
  'TellentProfileDropdownConfig',
);

export const provideTellentProfileDropdownConfig = (
  factory: TellentProfileDropdownConfigFactory,
) => ({
  provide: TellentProfileDropdownConfigToken,
  useFactory: factory,
});

export const injectTellentProfileDropdownConfig = () => inject(TellentProfileDropdownConfigToken);

export const provideTellentProfileDropdownConfigFactory = () =>
  provideTellentProfileDropdownConfig(() => {
    const store = inject(Store);
    const switchStrategy = inject(CompanySwitchStrategy);
    const tellentRouting = inject(TellentNavigationRoutingService);

    const companyVerificationDataService = inject(CompanyVerificationDataService);
    const currentMember = store.selectSignal(
      TellentCurrentUserSelectors.currentOrganizationMembershipSelector,
    );
    const currentSeat = computed(() => currentMember().seats[0]!);
    const currentUser = <Signal<TellentCurrentUser>>(
      store.selectSignal(TellentCurrentUserSelectors.data)
    );
    const companies = computed(() => currentUser()!.members);

    const currentOrg = computed(() => {
      const user = currentUser();
      return user.members.find(m => m.org.guid === user.currentOrgGuid)?.org;
    });

    const companyVerificationStatus = toSignal(companyVerificationDataService.status$);

    return {
      companies,
      currentUser,
      currentMember,
      currentSeat,
      currentOrg,
      companyVerificationStatus,
      handlers: {
        switchCompany: orgGuid => switchStrategy.switchCompany(orgGuid),
        editProfile: (analyticsContext?: string) =>
          tellentRouting.navigateById({
            id: TELLENT_PROFILE_DIALOG_ID,
            params: {
              tab: UserProfileTab.Profile,
              analyticsContext: 'admin_dropdown',
            },
          }),
        manageCompanies: (analyticsContext?: string) =>
          tellentRouting.navigateById({
            id: TELLENT_PROFILE_DIALOG_ID,
            params: {
              tab: UserProfileTab.Companies,
              analyticsContext: 'admin_dropdown',
            },
          }),
      },
    };
  });
